import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Calendar, TrendingUp, TrendingDown, BarChart3, Clock, Target, Award, Lightbulb } from 'lucide-react'
import { MoodEntry } from '../types'
import { analyzeMoodPatterns, generateMonthlyReport } from '../utils/analytics'

interface ReportsProps {
  entries: MoodEntry[]
}

export default function Reports({ entries }: ReportsProps) {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth())
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())

  const monthlyReport = generateMonthlyReport(entries, selectedMonth, selectedYear)
  const patterns = analyzeMoodPatterns(entries)

  const months = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ]

  const weekdays = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato']

  if (entries.length === 0) {
    return (
      <motion.div 
        className="text-center py-16"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <BarChart3 className="w-20 h-20 text-gray-300 mx-auto mb-6" />
        <h3 className="text-xl font-bold text-gray-600 mb-3">
          Nessun Dato Disponibile 📊
        </h3>
        <p className="text-gray-500 max-w-sm mx-auto leading-relaxed">
          Inizia a tracciare il tuo umore per vedere report dettagliati e pattern interessanti
        </p>
      </motion.div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Month Selector */}
      <motion.div 
        className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-gray-200"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
      >
        <div className="flex items-center space-x-3 mb-4">
          <Calendar className="w-5 h-5 text-purple-600" />
          <h3 className="font-bold text-gray-800">Seleziona Periodo</h3>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="p-2 border border-gray-300 rounded-lg bg-white"
          >
            {months.map((month, index) => (
              <option key={index} value={index}>{month}</option>
            ))}
          </select>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="p-2 border border-gray-300 rounded-lg bg-white"
          >
            {[2024, 2023, 2022].map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </motion.div>

      {/* Monthly Summary */}
      <motion.div 
        className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 shadow-lg border border-purple-200"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <div className="flex items-center space-x-3 mb-4">
          <Award className="w-6 h-6 text-purple-600" />
          <h3 className="text-xl font-bold text-purple-800">
            Report {months[selectedMonth]} {selectedYear}
          </h3>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600">{monthlyReport.totalEntries}</div>
            <div className="text-sm text-purple-500">Giorni Tracciati</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-pink-600">{monthlyReport.averageMood}</div>
            <div className="text-sm text-pink-500">Umore Medio</div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
            <span className="text-gray-700">Giorno Migliore</span>
            <div className="flex items-center space-x-2">
              <TrendingUp className="w-4 h-4 text-purple-600" />
              <span className="font-semibold text-purple-600">{monthlyReport.bestDay || 'N/A'}</span>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 bg-white/50 rounded-lg">
            <span className="text-gray-700">Giorno più Difficile</span>
            <div className="flex items-center space-x-2">
              <TrendingDown className="w-4 h-4 text-red-600" />
              <span className="font-semibold text-red-600">{monthlyReport.worstDay || 'N/A'}</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Mood Patterns */}
      <motion.div 
        className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-gray-200"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <div className="flex items-center space-x-3 mb-4">
          <Lightbulb className="w-6 h-6 text-yellow-600" />
          <h3 className="text-xl font-bold text-gray-800">Pattern Scoperti</h3>
        </div>

        <div className="space-y-4">
          {/* Best Day of Week */}
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <h4 className="font-semibold text-purple-800 mb-2">💚 Il Tuo Giorno Preferito</h4>
            <p className="text-purple-700">
              {weekdays[patterns.bestDayOfWeek]} è il giorno in cui ti senti meglio! 
              Umore medio: <span className="font-bold">{patterns.bestDayAverage}</span>
            </p>
          </div>

          {/* Worst Day of Week */}
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <h4 className="font-semibold text-red-800 mb-2">💙 Giorno più Impegnativo</h4>
            <p className="text-red-700">
              {weekdays[patterns.worstDayOfWeek]} sembra essere più difficile. 
              Umore medio: <span className="font-bold">{patterns.worstDayAverage}</span>
            </p>
          </div>

          {/* Mood Trend */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-2">📈 Tendenza Generale</h4>
            <p className="text-blue-700">
              {patterns.trend === 'improving' && 'Il tuo umore sta migliorando nel tempo! 🌟'}
              {patterns.trend === 'declining' && 'Sembra che tu stia attraversando un periodo difficile. Ricorda che è normale! 🤗'}
              {patterns.trend === 'stable' && 'Il tuo umore è abbastanza stabile. Ottimo equilibrio! ⚖️'}
            </p>
          </div>

          {/* Consistency */}
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <h4 className="font-semibold text-purple-800 mb-2">🎯 Consistenza</h4>
            <p className="text-purple-700">
              {patterns.consistency > 0.8 && 'Sei molto costante nel tuo umore! 🎯'}
              {patterns.consistency > 0.6 && patterns.consistency <= 0.8 && 'Hai una buona stabilità emotiva 👍'}
              {patterns.consistency <= 0.6 && 'Le tue emozioni variano molto - è perfettamente normale! 🌈'}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Weekly Pattern Chart */}
      <motion.div 
        className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-gray-200"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        <div className="flex items-center space-x-3 mb-4">
          <Clock className="w-6 h-6 text-indigo-600" />
          <h3 className="text-xl font-bold text-gray-800">Pattern Settimanale</h3>
        </div>

        <div className="space-y-3">
          {patterns.weeklyPattern.map((dayData, index) => (
            <motion.div 
              key={index}
              className="flex items-center space-x-4"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.8 + index * 0.1 }}
            >
              <div className="w-20 text-sm font-medium text-gray-600">
                {weekdays[index]}
              </div>
              <div className="flex-1 bg-gray-200 rounded-full h-4 relative">
                <motion.div
                  className={`h-4 rounded-full ${
                    dayData.average >= 4 ? 'bg-purple-500' :
                    dayData.average >= 3 ? 'bg-yellow-500' :
                    dayData.average >= 2 ? 'bg-orange-500' : 'bg-red-500'
                  }`}
                  initial={{ width: 0 }}
                  animate={{ width: `${(dayData.average / 5) * 100}%` }}
                  transition={{ duration: 1, delay: 1 + index * 0.1 }}
                />
                <div className="absolute inset-0 flex items-center justify-end pr-2">
                  <span className="text-xs font-bold text-white">
                    {dayData.average}
                  </span>
                </div>
              </div>
              <div className="text-sm text-gray-500">
                {dayData.count} {dayData.count === 1 ? 'entry' : 'entries'}
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Insights & Recommendations */}
      <motion.div 
        className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-6 shadow-lg border border-yellow-200"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.8 }}
      >
        <div className="flex items-center space-x-3 mb-4">
          <Target className="w-6 h-6 text-orange-600" />
          <h3 className="text-xl font-bold text-orange-800">Consigli Personalizzati</h3>
        </div>

        <div className="space-y-3">
          {patterns.bestDayOfWeek !== patterns.worstDayOfWeek && (
            <div className="p-3 bg-white/50 rounded-lg">
              <p className="text-orange-700">
                💡 Prova a replicare le attività del {weekdays[patterns.bestDayOfWeek]} 
                anche negli altri giorni della settimana!
              </p>
            </div>
          )}
          
          {patterns.trend === 'declining' && (
            <div className="p-3 bg-white/50 rounded-lg">
              <p className="text-orange-700">
                🌱 Considera di dedicare più tempo alle attività che ti fanno stare bene. 
                Piccoli cambiamenti possono fare la differenza!
              </p>
            </div>
          )}

          {monthlyReport.totalEntries < 15 && (
            <div className="p-3 bg-white/50 rounded-lg">
              <p className="text-orange-700">
                📝 Prova a tracciare il tuo umore più regolarmente per ottenere 
                insights ancora più accurati!
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  )
}